import { Component, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ComponentBase } from 'src/app/components/core/_infrastructure/classes/componentBase';
import { ExceptionManager } from 'src/app/components/core/_infrastructure/classes/handlers/exceptionManager';
import { AuthService } from 'src/app/components/core/_infrastructure/classes/services/auth.service';
import { NotificationService } from 'src/app/components/core/_infrastructure/classes/services/notification.service';
import { NavMenuService } from '../../../../../core/components/navmenu/navmenu.service';
import { GlobalService } from '../../../../../core/_infrastructure/classes/services/global.service';
import { Tc } from '../../../../../core/_infrastructure/types/common';
import { PeriodoAmbitoService } from '../ambito.service';
import { PeriodoAmbitoCaCamViewModel, PeriodoAmbitoProvinciaCamViewModel, PeriodoAmbitoViewModel } from '../ambito.types';
import { PeriodoCamService } from '../../datosbasicos/periodocam.service';

@Component({
    selector: 'app-ambito-wrapper-edit',
    templateUrl: './ambito-wrapper-edit.component.html',
})
export class PeriodoAmbitoWrapperEditComponent extends ComponentBase implements OnInit {
    /**
     * @article: Reactive forms with smart dumb components
     * https://medium.com/angular-athens/reactive-forms-with-smart-dumb-components-48ebc81417c7
     */

    visibilitySpinner = false;
    ambitoPeriodo: PeriodoAmbitoViewModel;
    comunidades: Tc[];
    provincias: Tc[];
    esVigenteConSolicitudes = false;

    successMessage = 'Ámbito guardado correctamente';
    listUrl = '/mantenimiento/periodos';

    activatedRoute$ = this.activatedRoute.paramMap.subscribe(params => {
        const id: any = params.get('id');
        if (id) {
            this.getAmbitoPeriodo(id);
        }
    });

    constructor(
        private fb: FormBuilder,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private ambitoPeriodoService: PeriodoAmbitoService,
        private periodoCamService: PeriodoCamService,
        protected notificationService: NotificationService,
        private globalService: GlobalService,
        protected authService: AuthService,
        protected exceptionHandler: ExceptionManager,
        private navMenuService: NavMenuService,
    ) {
        super(authService, notificationService, exceptionHandler);
    }

    ngOnInit(): void {
        this.preparePresentation();
    }

    private preparePresentation(): void {
        this.buildForm();
    }

    private buildForm(): void {
        this.getComunidades();

        this.form = this.fb.group({
            Id: [0],
            IdCA: [null],
            IdProvincia: [null],
            AmbitoNacional: [true],
        });
    }

    private getComunidades(): void {
        this.globalService.getComunidadesAutonomas().subscribe((comunidades: Tc[]) => {
            this.comunidades = comunidades;
        });
    }

    /**
     * Invoked when the user selects a comunidad
     * @param grupoId number
     * @return void
     */
    onSelectComunidad(comunidadId: number): void {
        this.globalService.getProvinciasByComunidad(comunidadId).subscribe((provincias: Tc[]) => {
            this.provincias = provincias;
        });
    }

    onSelectAmbito() {
        let longComunidades = this.ambitoPeriodo.TPeriodoAmbitoCaCamCollection.length;
        let longProvincias = this.ambitoPeriodo.TPeriodoAmbitoProvinciaCamCollection.length;
        for (let i = 0; i < longComunidades; i++) {
            this.onDeleteComunidad(this.ambitoPeriodo.TPeriodoAmbitoCaCamCollection[i]);
        }
        for (let i = 0; i < longProvincias; i++) {
            this.onDeleteProvincia(this.ambitoPeriodo.TPeriodoAmbitoProvinciaCamCollection[i]);
        }
    }

    onAddComunidad(comunidad: PeriodoAmbitoCaCamViewModel) {
        //Comprobamos que no esté ya incluido
        let yaIncluido = this.ambitoPeriodo.TPeriodoAmbitoCaCamCollection.some(s => s.IdCA == comunidad.IdCA);
        if (yaIncluido) {
            return;
        }
        //Añadimos la Descripcion
        let comunidadSeleccionada = this.comunidades.find(s => s.Id == comunidad.IdCA);
        comunidad.DescripcionComunidad = comunidadSeleccionada!.Descripcion;

        this.form.patchValue({
            IdCA: null,
        });

        this.ambitoPeriodo.TPeriodoAmbitoCaCamCollection.push(comunidad);
    }

    onDeleteComunidad(comunidad: PeriodoAmbitoCaCamViewModel) {
        this.ambitoPeriodo.TPeriodoAmbitoCaCamCollection.splice(this.ambitoPeriodo.TPeriodoAmbitoCaCamCollection.indexOf(comunidad), 1);
        //se quitan las provincias de las comunidades que se eliminan
        let arrayProvincias = this.ambitoPeriodo.TPeriodoAmbitoProvinciaCamCollection.filter(p => p.IdComunidad == comunidad.IdCA);
        for (let i = 0; i < arrayProvincias.length; i++) {
            this.onDeleteProvincia(arrayProvincias[i]);
        }
    }

    onAddProvincia(provincia: PeriodoAmbitoProvinciaCamViewModel) {
        //Comprobamos que esté añadida su C.A.
        let caIncluida = this.ambitoPeriodo.TPeriodoAmbitoCaCamCollection.some(s => s.IdCA == provincia.IdComunidad);
        if (!caIncluida) {
            this.addErrorMessage('Debe incluir antes su Comunidad Autónoma');
            return;
        }
        //Comprobamos que no esté ya incluido
        let yaIncluido = this.ambitoPeriodo.TPeriodoAmbitoProvinciaCamCollection.some(s => s.IdProvincia == provincia.IdProvincia);
        if (yaIncluido) {
            return;
        }
        //Añadimos la Descripcion
        let provinciaSeleccionada = this.provincias.find(s => s.Id == provincia.IdProvincia);
        provincia.DescripcionProvincia = provinciaSeleccionada!.Descripcion;

        this.form.patchValue({
            IdProvincia: null,
        });

        this.ambitoPeriodo.TPeriodoAmbitoProvinciaCamCollection.push(provincia);
    }

    onDeleteProvincia(provincia: PeriodoAmbitoProvinciaCamViewModel) {
        this.ambitoPeriodo.TPeriodoAmbitoProvinciaCamCollection.splice(this.ambitoPeriodo.TPeriodoAmbitoProvinciaCamCollection.indexOf(provincia), 1);
    }

    /**
     * Método que invoca a la API para cargar los datos de una ámbito a partir de su id.
     * @param id number
     * @return void
     */
    private getAmbitoPeriodo(id: number): void {
        this.ambitoPeriodoService
            .getAmbitoPeriodo(id)
            .pipe(
                tap((a: PeriodoAmbitoViewModel) => {
                    this.ambitoPeriodo = a;
                    this.ambitoPeriodoService.ambitoPeriodo = this.ambitoPeriodo;
                    this.navMenuService.updateMenuServiceForPeriodo(a.Id!);
                    if (this.esPeriodoFinalizado) {
                        this.form.disable();
                        return;
                    }
                    this.getSiVigenteConSolicitudes(id);
                }),
                catchError(error => {
                    this.catchServerError(error);
                    this.form.disable();
                    return of(null);
                }),
            )
            .subscribe();
    }

    /**
     * Método que invoca a la API para comprueba si el periodo es vigente y tiene solicitudes de campaña asociadas.
     * @param id number
     * @return void
     */
    private getSiVigenteConSolicitudes(id: number): void {
        this.periodoCamService
            .getPeriodoEsVigente(id)
            .pipe(
                tap((p: boolean) => {
                    this.esVigenteConSolicitudes = p;
                }),
                catchError(error => {
                    this.catchServerError(error);
                    return of(null);
                }),
            )
            .subscribe();
    }

    /**
     * Invocación de la API para salvar los cambios realizados en el ámbito
     * @return void
     */
    onSave(): void {
        //validación Comunidad si Ambito NO Nacional y no hay Comunidades en la lista
        if (!this.form.value.AmbitoNacional && this.ambitoPeriodo.TPeriodoAmbitoCaCamCollection.length == 0) {
            this.addErrorMessage('Debe añadir alguna Comunidad Autónoma');
            return;
        }
        this.visibilitySpinner = true;
        const model: PeriodoAmbitoViewModel = this.form.getRawValue();

        model.TPeriodoAmbitoCaCamCollection = this.ambitoPeriodo?.TPeriodoAmbitoCaCamCollection;
        model.TPeriodoAmbitoProvinciaCamCollection = this.ambitoPeriodo?.TPeriodoAmbitoProvinciaCamCollection;

        this.ambitoPeriodoService
            .saveAmbitoPeriodo(model)
            .pipe(
                tap(() => {
                    this.visibilitySpinner = false;
                    this.addSuccessMessage(this.successMessage);
                    this.onBack();
                }),
                catchError(error => {
                    this.visibilitySpinner = false;
                    this.catchServerError(error);
                    return of(null);
                }),
            )
            .subscribe();
    }

    onBack(): void {
        this.router.navigate([this.listUrl]);
    }

    get formInvalid(): boolean {
        return !this.form.valid || !this.form.dirty || this.form.pristine;
    }

    get esPeriodoFinalizado(): boolean {
        const hoy = new Date();
        if (this.ambitoPeriodo?.PeriodoFechaFin) {
            return hoy >= new Date(this.ambitoPeriodo.PeriodoFechaFin);
        } else {
            return false;
        }
    }
}
